/**
 * useCreditInfo — fetches and caches the current user's credit balance
 * from GET /api/credits.
 *
 * Returns:
 *   { balance, tier, packPrice, creditsPerPurchase, hasCreditHistory, loading, resolved, refetch }
 */
import { useState, useEffect, useCallback } from 'react';
import { getCreditInfo } from '../utils/api';
import type { Tier } from '../utils/types';

export interface CreditInfo {
  /**
   * null = unlimited. The backend maps an Infinity balance to null
   * (api/routes/credits.ts), which happens for PRIVISY_PRO_USER_IDS members
   * and for admins (middleware/freemium.ts attachCreditInfo). Read it
   * together with `resolved` — see UNKNOWN_CREDIT_INFO below.
   */
  balance: number | null;
  tier: Tier;
  /**
   * Pack pricing, straight from the API. `undefined` means "not known yet",
   * never "probably the usual numbers": both are env-configurable
   * (CREDIT_PACK_PRICE_CENTS, CREDITS_PER_PURCHASE), so a local fallback
   * would advertise stale numbers while Stripe charges the current ones
   * (#970 bug 2). Nothing in the frontend may substitute a guess —
   * scripts/check-purchase-funnel.js enforces that across every module,
   * this one included.
   */
  packPrice?: number;
  creditsPerPurchase?: number;
  /**
   * The account has held credits it could spend (#1776). False only for an
   * account whose purchases were all "unlock this report" checkouts; the
   * header then shows no balance. `undefined` until the API answers, and
   * consumers treat that like true, so a failed fetch never hides "Buy
   * Credits" from someone who has credits.
   */
  hasCreditHistory?: boolean;
}

export interface UseCreditInfoResult extends CreditInfo {
  loading: boolean;
  /**
   * True only once GET /api/credits has actually answered. False while the
   * request is in flight, false after it fails, and false when there is no
   * signed-in account to read.
   *
   * This is the discriminator issue #970 was missing. Every other field can
   * hold a value that is *also* a legitimate real value — `balance: null`
   * most of all, because it means UNLIMITED — so consumers must branch on
   * this flag rather than on the values. `paywallStateFor` takes it as a
   * required argument for exactly that reason: a call site that forgets it
   * fails to compile instead of silently telling a free user they have
   * unlimited access.
   */
  resolved: boolean;
  refetch: () => void;
}

/**
 * The unknown state: what the hook reports before (and instead of) an answer.
 *
 * Deliberately asserts nothing. `balance: null` is kept only because the
 * shape needs *some* value and every alternative is equally a real one
 * (0 = out of credits, a number = has credits); `resolved: false` is what
 * makes it unreadable as an account, and no consumer may interpret these
 * fields without it. Pack pricing is absent rather than guessed.
 *
 * `loading` is intentionally *not* pinned true on failure: consumers such as
 * LockedReportView gate whole pages on it, and a permanent spinner would
 * trade a wrong answer for a hung one. An explicit flag lets each surface
 * decide what "unknown" should look like.
 */
const UNKNOWN_CREDIT_INFO: CreditInfo & { resolved: false } = {
  balance: null,
  tier: 'free',
  packPrice: undefined,
  creditsPerPurchase: undefined,
  hasCreditHistory: undefined,
  resolved: false,
};

export function useCreditInfo(authEnabled = false, isSignedIn = true): UseCreditInfoResult {
  const [data, setData] = useState<CreditInfo & { resolved: boolean }>(UNKNOWN_CREDIT_INFO);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (authEnabled && !isSignedIn) {
      setData(UNKNOWN_CREDIT_INFO);
      setLoading(false);
      return;
    }

    let canceled = false;
    setLoading(true);

    getCreditInfo()
      .then((json) => {
        if (!canceled) {
          setData({
            balance: json.balance ?? null,
            tier: json.tier ?? 'free',
            packPrice: json.packPrice,
            creditsPerPurchase: json.creditsPerPurchase,
            hasCreditHistory: json.hasCreditHistory !== false,
            resolved: true,
          });
        }
      })
      .catch(() => {
        // API unreachable — report the account as unknown rather than
        // inventing one. Anything else here is a guess a consumer cannot
        // detect (#970).
        if (!canceled) setData(UNKNOWN_CREDIT_INFO);
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, [tick, authEnabled, isSignedIn]);

  return { ...data, loading, refetch };
}
